"use client";

import {useCallback, useEffect, useSyncExternalStore} from "react";
import {apiFetchWithStatus} from "@/lib/api";
import type {ModelCatalogModel} from "@/types/modelCatalog";
import {
   beginModelCatalogLoad,
   failModelCatalogLoad,
   getModelCatalogSnapshot,
   isCurrentModelCatalogLoad,
   MODEL_CATALOG_REVALIDATE_MS,
   setModelCatalog,
   subscribeModelCatalog,
} from "@/utils/ai/modelCatalog";
import {GALACTUS_INTERNAL_API} from "@/utils/common/constants/routes";

export const MODEL_CATALOG_URL = `${GALACTUS_INTERNAL_API}/model-catalog/models`;

type UseModelCatalogResult = {
   models: ModelCatalogModel[];
   isLoading: boolean;
   error: string | null;
   refresh: () => Promise<void>;
   revalidate: () => Promise<void>;
};

let inflight: Promise<void> | null = null;

async function fetchModelCatalog(): Promise<ModelCatalogModel[]> {
   const response = await apiFetchWithStatus<ModelCatalogModel[]>(MODEL_CATALOG_URL, "GET");
   if (!response.success || !Array.isArray(response.data)) {
      throw new Error(response.statusText || "Failed to load model catalog");
   }
   return response.data;
}

function shouldRevalidate(fetchedAt: number | null, now = Date.now()): boolean {
   return fetchedAt == null || now - fetchedAt >= MODEL_CATALOG_REVALIDATE_MS;
}

function ensureModelCatalog(force = false): Promise<void> {
   const current = getModelCatalogSnapshot();
   if (!force && !shouldRevalidate(current.fetchedAt)) {
      return Promise.resolve();
   }
   if (inflight) {
      return inflight;
   }

   const generation = beginModelCatalogLoad();
   inflight = fetchModelCatalog()
      .then((models) => {
         if (!isCurrentModelCatalogLoad(generation)) {
            return;
         }
         setModelCatalog(models);
      })
      .catch((err: unknown) => {
         const message = err instanceof Error ? err.message : "Failed to load model catalog";
         failModelCatalogLoad(generation, message);
      })
      .finally(() => {
         inflight = null;
      });
   return inflight;
}

/** Test isolation only. */
export function __resetModelCatalogFetch(): void {
   inflight = null;
}

export function useModelCatalog(): UseModelCatalogResult {
   const snapshot = useSyncExternalStore(
      subscribeModelCatalog,
      getModelCatalogSnapshot,
      getModelCatalogSnapshot
   );

   const revalidate = useCallback(() => ensureModelCatalog(false), []);
   const refresh = useCallback(() => ensureModelCatalog(true), []);

   useEffect(() => {
      void ensureModelCatalog(false);
   }, []);

   return {
      models: snapshot.models,
      isLoading: snapshot.isLoading,
      error: snapshot.error,
      refresh,
      revalidate,
   };
}
